import { useLazyQuery, useMutation } from '@apollo/client';
import { Button, Card, Form, Space } from 'antd';
import { capitalize, map } from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { AppContext } from '../../../AppContext';
import { MODULES } from '../../../common/constants';
import { fileUpload, openNotification } from '../../../common/utils';
import LoaderComponent from '../../../components/LoaderComponent';
import Portal from '../../../components/Portal';
import { UPDATE_CURRENT_USER } from '../../auth/graphql/Mutations';
import { GET_CURRENT_USER } from '../../auth/graphql/Queries';
import ChangePasswordModal from '../components/ChangePasswordModal';
import ProfileForm from '../components/ProfileForm';
import { GET_SIGNED_URL } from '../graphql/Queries';

function Profile() {
  const { dispatch, getCurrentUser } = useContext(AppContext);
  const { firstName = '', lastName = '', id } = getCurrentUser() || {};

  const [btnLoading, setBtnLoading] = useState(false);

  const [form] = Form?.useForm();
  const [isDisabled, setIsDisabled] = useState(true);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [userProfilePic, setUserProfilePic] = useState([]);
  const [userData, setUserData] = useState({});
  const [isUserDataLoading, setIsUserDataLoading] = useState(false);

  const [fetchCurrentUser] = useLazyQuery(GET_CURRENT_USER, {
    onCompleted: (response) => {
      setUserData(response?.currentUserProfile);
      const imageUrl = response?.currentUserProfile?.profileImage;
      if (imageUrl) {
        setUserProfilePic([{ url: imageUrl }]);
      }
      setIsUserDataLoading(false);
    },
    fetchPolicy: 'network-only',
    onError() {},
  });

  const [getSignUrl] = useLazyQuery(GET_SIGNED_URL, {
    fetchPolicy: 'network-only',
    onError() {
      setBtnLoading(false);
    },
  });

  const [updateCurrentUser] = useMutation(UPDATE_CURRENT_USER, {
    onError: () => {
      form?.setFieldsValue(userData);
      setBtnLoading(false);
    },
  });

  useEffect(() => {
    setIsUserDataLoading(true);
    fetchCurrentUser();
  }, []);

  const handleDiscard = () => {
    setIsUserDataLoading(true);
    fetchCurrentUser();
    setIsDisabled(true);
    dispatch({
      type: 'SET_SHOW_PROMPT',
      data: false,
    });
  };

  const onFinish = async (values) => {
    setBtnLoading(true);
    const uuid = uuidv4();
    const photoKeys = {
      profileImage: '',
    };
    let images = {};
    dispatch({
      type: 'SET_SHOW_PROMPT',
      data: false,
    });

    const userObj = {
      firstName: values?.firstName?.trim(),
      lastName: values?.lastName?.trim(),
      contactNo: values?.contactNo,
      gender: values?.gender,
      address: values?.address?.trim(),
      address2: values?.address2?.trim(),
      locality: values?.locality?.trim(),
      landmark: values?.landmark?.trim(),
      state: values?.state,
      pincode: values?.userPincode,
      city: values?.city,
    };

    if (!values?.profileImage?.url && userProfilePic?.length > 0) {
      const documentArray = [
        {
          name: 'profile-photo',
          value: values?.profileImage,
        },
      ];
      await Promise.all(
        map(documentArray, async (item) => {
          if (item?.value && typeof item?.value === 'object') {
            const { name } = item?.value?.[0];
            const ext = name?.substring(name?.lastIndexOf('.') + 1);
            const timestamp = Date?.now();
            const filename = name?.split('.')?.slice(0, -1)?.join('.');
            const newFilename = `${timestamp}_${filename}.${ext}`;
            const fileKey = `profile/${uuid}/${newFilename}`;

            const res = await getSignUrl({
              variables: {
                data: {
                  fileName: fileKey,
                  userId: id,
                },
              },
            });

            if (res) {
              const {
                signedUrl,
                fileName,
              } = res?.data?.getUserProfileImageSignedPutUrl;

              try {
                await fileUpload(
                  signedUrl,
                  item?.value?.[0]?.originFileObj,
                )?.catch((error) => {
                  if (error === 403) {
                    openNotification(
                      'error',
                      `${item?.name
                        ?.toString()
                        ?.toUpperCase()} upload failed. Please try again.`,
                    );
                  }
                });
                switch (item?.name) {
                  case 'profile-photo':
                    photoKeys.profileImage = fileName;
                    break;
                  default:
                    break;
                }
              } catch (error) {
                setBtnLoading(false);
                return error;
              }
            }
          }
        }),
      ).then(() => {
        images = {
          ...photoKeys,
        };
      });
    }
    try {
      const res = await updateCurrentUser({
        variables: {
          where: { id },
          data: {
            ...userObj,
            profileImage:
              typeof values?.profileImage !== 'string'
                ? images?.profileImage || ''
                : undefined,
          },
        },
      });

      if (res?.data) {
        setIsUserDataLoading(true);
        fetchCurrentUser();
      }
    } catch (error) {
      setBtnLoading(false);
      return error;
    }
    setBtnLoading(false);
    setIsDisabled(true);
  };

  const initialValues = {
    ...userData,
    state: userData?.state,
    city: userData?.city,
    userPincode: userData?.pincode?.pincode,
  };

  return (
    <>
      <Portal portalId="header-title-content">
        <div className="portal-header">{MODULES?.PROFILE}</div>
      </Portal>
      <Portal portalId="header-right-content">
        <div key="actionbutton" className="text-right">
          <Space>
            <Button
              onClick={handleDiscard}
              disabled={btnLoading || isDisabled}
              type="primary"
              className="common-button mr-16"
            >
              Discard Changes
            </Button>
            <Button
              onClick={form.submit}
              loading={btnLoading}
              disabled={isDisabled}
              htmlType="submit"
              className="common-button"
            >
              Save
            </Button>
          </Space>
        </div>
      </Portal>
      <Portal portalId="header-right-content-phones">
        <div key="actionbutton" className="text-right">
          <Space>
            <Button
              onClick={handleDiscard}
              disabled={btnLoading || isDisabled}
              type="primary"
              className="common-button mr-16"
            >
              Discard Changes
            </Button>
            <Button
              onClick={form.submit}
              loading={btnLoading}
              disabled={isDisabled}
              htmlType="submit"
              className="common-button"
            >
              Save
            </Button>
          </Space>
        </div>
      </Portal>
      <ChangePasswordModal
        visible={isPasswordVisible}
        setVisible={setIsPasswordVisible}
      />
      <Card
        className="full-height-with-nav"
        title={`${capitalize(firstName)} ${capitalize(lastName)} Details`}
        extra={
          <Button
            type="primary"
            size="small"
            onClick={() => setIsPasswordVisible(true)}
            className="change-password-btn"
          >
            Change Password
          </Button>
        }
      >
        {isUserDataLoading ? (
          <LoaderComponent
            size="large"
            setHeight="60"
            spinning={isUserDataLoading}
          />
        ) : (
          <div className="library-form">
            <ProfileForm
              form={form}
              setIsDisabled={setIsDisabled}
              onFinish={onFinish}
              initialValues={initialValues}
              userProfilePic={userProfilePic}
              userData={userData}
              setUserProfilePic={setUserProfilePic}
            />
          </div>
        )}
      </Card>
    </>
  );
}
export default Profile;
